<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">
        {{ editing ? "Editar colaborador" : "Nuevo colaborador" }}
      </h1>
    </div>
    <div>
      <form class="custom-form" @submit.prevent="save" novalidate>
        <b-card
          tag="article"
          no-body
          class="custom-card"
        >
          <b-card-header
            header-tag="section"
            class="custom-card-header d-flex align-items-center"
          >
            <h3>
              {{ editing ? "Datos del colaborador" : "Datos del nuevo colaborador" }}
            </h3>
          </b-card-header>

          <b-card-body class="custom-card-body p-0">
            <b-tabs card v-model="activeTab" class="employee-tabs">

              <!-- Tab 1: Datos personales -->
              <b-tab>
                <template #title>
                  <i class="ri-user-line mr-1"></i>Datos personales
                </template>
                <PersonalDataTab
                  :form="form"
                  :v="$v.form"
                  :districts="districts"
                  :nationalityList="nationalityList"
                  :identifierTypes="identifierTypes"
                  :genderList="genderList"
                />
              </b-tab>

              <!-- Tab 2: Contacto y domicilio -->
              <b-tab>
                <template #title>
                  <i class="ri-map-pin-line mr-1"></i>Contacto y domicilio
                </template>
                <ContactTab
                  :form="form"
                  :v="$v.form"
                  :districts="districts"
                />
              </b-tab>

              <!-- Tab 3: Banco y pensiones -->
              <b-tab>
                <template #title>
                  <i class="ri-bank-card-line mr-1"></i>Banco y pensiones
                </template>
                <BankAndPensionsTab
                  :form="form"
                  :v="$v.form"
                  :banks="banks"
                  :pensionEntities="pensionEntities"
                />
              </b-tab>

              <!-- Tab 4: Académico -->
              <b-tab>
                <template #title>
                  <i class="ri-graduation-cap-line mr-1"></i>Académico
                </template>
                <AcademicTab
                  :form="form"
                  :v="$v.form"
                />
              </b-tab>

            </b-tabs>

            <div class="form-group-buttons text-right px-4 pb-4">
              <router-link
                :to="editing ? { name: 'employee-details', params: { id: $route.params.id } } : { name: 'employee-list' }"
                class="button button-light"
              >
                {{ editing ? "Volver" : "Cancelar" }}
              </router-link>
              <button class="button button-primary" type="submit">
                Guardar
              </button>
            </div>
          </b-card-body>
        </b-card>
      </form>
    </div>
  </div>
</template>

<script>
import { Alert, CustomValidators } from 'wize-admin'
import { email, numeric, required, requiredIf, minLength } from 'vuelidate/lib/validators'
import { mapGetters } from 'vuex'

import {
  EmployeeService,
  UbigeoService,
  CountryService,
  BankService,
  PensionEntityService,
} from '@/core/services'
import { Constants } from '@/core/utils'

import PersonalDataTab    from './tabs/PersonalDataTab.vue'
import ContactTab         from './tabs/ContactTab.vue'
import BankAndPensionsTab from './tabs/BankAndPensionsTab.vue'
import AcademicTab        from './tabs/AcademicTab.vue'

export default {
  components: {
    PersonalDataTab,
    ContactTab,
    BankAndPensionsTab,
    AcademicTab,
  },

  data() {
    return {
      loading:    true,
      editing:    false,
      activeTab:  0,

      // Listas para los selects
      districts:       [],
      nationalityList: [],
      banks:           [],
      pensionEntities: [],

      // Listas estáticas pasadas a PersonalDataTab
      identifierTypes: [
        Constants.IdentifierType.DNI,
        Constants.IdentifierType.CE,
        Constants.IdentifierType.PASAPORTE,
        Constants.IdentifierType.LIBRETA_MILITAR,
        Constants.IdentifierType.CARNET_MILITAR,
      ],
      genderList: [
        { key: 1, label: 'MASCULINO' },
        { key: 2, label: 'FEMENINO' },
      ],

      form: {
        // Identidad
        identifierType:   null,
        identifier:       null,
        name:             null,
        lastNamePaternal: null,
        lastNameMaternal: null,
        birthDate:        null,
        gender:           null,
        maritalStatus:    null,
        nationality:      null,
        bloodType:        null,
        hasDisability:    false,
        disabilityType:   null,
        conadisCertificate: null,
        // Contacto
        address:                      null,
        ubigeo:                       null,
        birthUbigeo:                  null,
        personalPhone:                null,
        personalEmail:                null,
        emergencyContact:             null,
        emergencyContactRelationship: null,
        emergencyPhoneNumber:         null,
        // Banco
        bankIdentifierType:   null,
        bankIdentifier:       null,
        bankHolder:           null,
        bankId:               null,
        bankAccount:          null,
        accountType:          null,
        bankInterAccount:     null,
        bankAccountVerified:  false,
        bankAccountVerifiedBy: null,
        salaryCurrency:       'PEN',
        ctsBankId:            null,
        ctsAccount:           null,
        ctsCurrency:          'PEN',
        // Pensión
        pensionSystem:      null,
        pensionEntityId:    null,
        cusppCode:          null,
        afpCommissionType:  null,
        // Tributario
        fifthCategoryStatus: null,
        // Académico
        educationLevel: null,
        profession:     null,
      },
    }
  },

  validations() {
    return {
      form: {
        identifierType:   { required },
        identifier:       { required, numeric },
        name:             { required },
        lastNamePaternal: { required },
        birthDate:        { required },
        gender:           { required },
        disabilityType:   { required: requiredIf(() => this.form.hasDisability) },
        conadisCertificate: { required: requiredIf(() => this.form.hasDisability) },
        personalPhone:    { numeric, mobile: CustomValidators.mobile },
        emergencyPhoneNumber: { numeric, mobile: CustomValidators.mobile },
        personalEmail:    { email },
        bankIdentifierType: { required: requiredIf(() => !!this.form.bankId) },
        bankIdentifier:     { required: requiredIf(() => !!this.form.bankId) },
        bankHolder:         { required: requiredIf(() => !!this.form.bankId) },
        accountType:        { required: requiredIf(() => !!this.form.bankId) },
        bankAccount:        { required: requiredIf(() => !!this.form.bankId) },
        bankInterAccount:   { numeric, minLength: minLength(20) },
      },
    }
  },

  watch: {
    '$route.params.id': function () {
      if (this.$route.params.id) this.load(this.$route.params.id)
    },
    'form.hasDisability': function (val) {
      if (!val) {
        this.form.disabilityType = null
        this.form.conadisCertificate = null
      }
    },
    'form.bankId': function (val) {
      if (!val) {
        this.form.accountType = null
        this.form.bankAccount = null
        this.form.bankInterAccount = null
      }
    },
    'form.name': function () { this.updateBankHolder() },
    'form.lastNamePaternal': function () { this.updateBankHolder() },
    'form.lastNameMaternal': function () { this.updateBankHolder() },
    'form.identifierType': function (val) {
      this.form.bankIdentifierType = val
    },
    'form.identifier': async function (val) {
      this.form.bankIdentifier = val
      if (this.loading) return
      this.$v.form.identifier.$touch()
      if (this.$v.form.identifier.$error) return
      if (
        this.form.identifierType === Constants.IdentifierType.DNI.key &&
        val && val.length === 8
      ) {
        try {
          this.$store.dispatch('app/loading', true)
          const resp = (await EmployeeService.queryDni(val)).payload
          this.form.name = resp.name
          this.form.lastNamePaternal = resp.surname
        } catch (ex) {
          Alert.error(ex.errors ? ex.errors.message : 'Error al consultar DNI')
        } finally {
          this.$store.dispatch('app/loading', false)
        }
      }
    },
    'form.bankAccountVerified': function () {
      if (!this.form.bankAccountVerifiedBy) {
        this.form.bankAccountVerifiedBy = this.user.email
      }
    },
  },

  computed: {
    ...mapGetters({ user: 'auth/getUser' }),
    employeeId() {
      return this.$route.params.id ? Number(this.$route.params.id) : null
    },
  },

  methods: {
    updateBankHolder() {
      this.form.bankHolder = [this.form.name, this.form.lastNamePaternal, this.form.lastNameMaternal]
        .filter(Boolean).join(' ')
    },
    async save() {
      this.$v.$touch()
      if (this.$v.$error) {
        this.$nextTick(() => {
          this.$smoothScroll({
            scrollTo: document.querySelector('.form-error'),
            updateHistory: false,
            offset: -50,
          })
        })
        return
      }

      try {
        this.$store.dispatch('app/loading', true)
        const data = { ...this.form }
        Object.keys(data).forEach(key => {
          if (data[key] === '') data[key] = null
        })
        let response
        if (!this.editing) response = await EmployeeService.save(data)
        else response = await EmployeeService.update(this.$route.params.id, data)
        if (response.status === 'BAD_REQUEST') {
          console.log('Validation errors:', JSON.stringify(response.payload))
          console.log('Payload enviado:', JSON.stringify(data))
          Alert.error('Error de validación: revise los campos')
          return
        }
        Alert.success('Se almacenó la información correctamente')
        const savedId = response.payload ? response.payload.id : this.$route.params.id
        this.$router.push({ name: 'employee-details', params: { id: String(savedId) } })
      } catch (ex) {
        console.log('Error response:', ex.response?.data)
        console.log('Error status:', ex.response?.status)
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al guardar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },

    async load(id) {
      try {
        this.$store.dispatch('app/loading', true)
        const payload = (await EmployeeService.get(id)).payload
        this.form = {
          ...payload,
          bankId:          payload.bank          ? payload.bank.id          : null,
          ctsBankId:       payload.ctsBank       ? payload.ctsBank.id       : null,
          pensionEntityId: payload.pensionEntity ? payload.pensionEntity.id : null,
        }
        this.editing = true
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar')
        this.$router.push({ name: 'employee-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
        setTimeout(() => { this.loading = false }, 1000)
      }
    },
  },

  async created() {
    if (this.$route.params.id) this.load(this.$route.params.id)
    else this.loading = false

    try {
      const [districts, countries, banks, pensionEntities] =
        await Promise.all([
          UbigeoService.listDistricts(),
          CountryService.list(),
          BankService.list(),
          PensionEntityService.list(),
        ])

      this.districts       = (districts.payload || []).map(b => ({ key: b.id, label: b.description }))
      this.nationalityList = (countries.payload || []).map(c => ({ key: c.id, label: c.description }))
      this.banks           = (banks.payload     || []).map(b => ({ key: b.id, label: b.name }))
      this.pensionEntities = (pensionEntities.payload || []).map(p => ({ key: p.id, label: `${p.code} - ${p.name}` }))
    } catch (ex) {
      console.error(ex)
      Alert.error('Error al cargar los catálogos')
    }
  },
}
</script>

<style lang="stylus" scoped>
.employee-tabs
  ::v-deep .nav-tabs
    border-bottom: 1px solid #dee2e6
    flex-wrap: nowrap
    overflow-x: auto
    .nav-link
      white-space: nowrap
</style>
